<script>
  import { onMount } from "svelte"
  import ApplicationState from "../../../../Stores/applicationStates"
  import Navbar from "../../../../Shared/Navbar/index.svelte"
  import Footer from "../../../../Shared/footer.svelte"
  import SinglePublish, { updateSinglePublish } from "../Stores/singlePublishStore"
  import notify from "../../../../Script/notify"
  import LoadingScreen from "../Components/loadingScreen.svelte"
  import { querystring, push } from "svelte-spa-router"
  import { queryStringToJSON } from "../../../../Script/queryString"
  import ShareComponent from "../../../../Shared/shareComponent.svelte"
  import QRCode from "qrcode"
  import RevokeConfirm from "../../../../Shared/revokeConfirm.svelte"
  import { postRequest } from "../../../../Script/apiMethods"

  /**
   * @type {Object} - object get from window location
   */
  let query = {}

  /**
   * @type {Boolean} - whether to disable next button or not
   */
  let apiCallBtnDisable = false

  let qrImage = ""

  /**
   * @type {Boolean} - whether to show a revoke OTP model or not
   */
  let revokeOTPModal = false

  /**
   * fetch new document id details or store qrcode value to local storage
   */
  onMount(async () => {
    try {
      query = queryStringToJSON($querystring)
      if (!qrImage) {
        if ($SinglePublish.datePublished) qrImage = await QRCode.toDataURL($SinglePublish.proposedURL)
      }
    } catch (error) {
      const info = "PAGE: Single Publish (Published Details Preview)\nEVENT: onMount"
      notify.danger(error, info)
    }
  })

  /**
   * @description function for generating revokation signature.
   */
  const handleRevocation = async (e) => {
    const revokeDetails = e.detail
    revokeOTPModal = false
    apiCallBtnDisable = true
    try {
      const CONFIG = {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      }

      const { signature } = await postRequest(
        `/api/swagger/v2/misc/proxy-sign`,
        {
          msg: $SinglePublish.documentId,
          otp: revokeDetails.code,
        },
        CONFIG
      )

      await postRequest(
        `/api/swagger/v2/documents/${$SinglePublish.documentId}/revoke`,
        {
          signature,
          revokeReason: revokeDetails.revokeReason,
          hideRevokedDoc: revokeDetails.hideRevokedDoc,
        },
        CONFIG
      )

      notify.success("Document <strong>revoked</strong> Successfully")
      localStorage.removeItem("P2BSinglePublish")
      $SinglePublish = {}
      push("/publish/single")
    } catch (error) {
      // console.error(error)
      const info = "PAGE: Single Publish (Published Details Preview)\nEVENT: During Published document revokation"
      notify.danger(error, info)
    } finally {
      apiCallBtnDisable = false
      updateSinglePublish($SinglePublish)
    }
  }

  /**
   * @description function for start a new single publish process.
   */
  const startNew = async () => {
    localStorage.removeItem("P2BSinglePublish")
    $SinglePublish = {}
    push("/publish/single")
    updateSinglePublish($SinglePublish)
  }
</script>

<svelte:window
  on:load={() => {
    push(`/publish/single?documentId=${query.documentId}`)
  }}
/>

<svelte:head>
  <title>Single Document Publish</title>
</svelte:head>

<div class="relative min-h-screen w-full overflow-hidden bg-[#f7f7f7] dark:bg-bodyblack">
  <Navbar />

  {#if revokeOTPModal}
    <RevokeConfirm
      on:submit={handleRevocation}
      on:close={() => {
        revokeOTPModal = false
      }}
    />
  {/if}

  <div class="px-6">
    <div class="mx-auto h-auto w-full max-w-[920px] pb-40 pt-6">
      <div class="mb-3 mt-6 flex items-end justify-end">
        <button
          on:click={() => {
            revokeOTPModal = !revokeOTPModal
          }}
          type="button"
          tabindex="0"
          data-tooltip="Revoke document"
          tooltip-position="top"
          class="flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-full border border-[#a0a1a580] bg-white px-2 text-sm font-medium text-gray-900 hover:bg-red-600 hover:text-white focus:outline-none focus-visible:bg-red-600 focus-visible:text-white focus-visible:outline-none active:bg-red-500 disabled:cursor-not-allowed dark:bg-black dark:text-white dark:hover:bg-red-600"
        >
          <i class="fa-solid fa-trash-can" />
        </button>
      </div>
      <div class="relative flex min-h-[470px] flex-col justify-between gap-3 rounded-md bg-white p-6 dark:bg-mdGray mobile:p-12">
        <div class="select-none space-y-3">
          <h1 class="text-3xl font-bold leading-snug dark:text-white mobile:text-5xl">Published document</h1>

          <p class="w-full break-words text-justify text-sm text-gray-500 dark:text-gray-300 mobile:text-base sm:w-3/5">Yay! You've successfully published the document on the blockchain! You can now view it by simply scanning the QR code or clicking the link below.</p>
        </div>

        <div class="flex items-center justify-end">
          <div class="flex flex-col items-center gap-3 sm:flex-row">
            <div class="order-2 w-full space-y-3 sm:order-1 sm:w-3/5">
              <div>
                <a href={$SinglePublish.proposedURL} target="_blank" class="flex break-all text-center text-blue-500 underline hover:text-blue-600 active:text-blue-400 sm:text-start">{$SinglePublish.proposedURL}</a>
                <p class="hidden select-none text-center text-gray-200 mobile:block sm:text-start">Click URL to open in new tab</p>
              </div>
              <div class="flex justify-center py-3 sm:justify-start">
                <ShareComponent Link={$SinglePublish.proposedURL} />
              </div>
            </div>
            <div class="order-1 flex w-full select-none flex-col items-center justify-center gap-3 rounded-md py-6 sm:order-2 sm:w-2/5">
              <img src={qrImage} alt="qrcode" class="w-3/5 rounded-md" />
            </div>
          </div>
        </div>
        <div class="flex flex-col items-center justify-center gap-3 md:flex-row">
          <a href="#/analytics/documents/single?id={query.documentId}" target="_blank" class="flex h-9 w-4/5 min-w-fit cursor-pointer items-center justify-center rounded-md border border-[#a0a1a580] bg-[#ff6a00] px-2 text-sm font-medium text-white hover:bg-[#ff8000] hover:text-white focus:outline-none active:bg-[#ff6a00] disabled:cursor-not-allowed disabled:bg-[#ff6a00] sm:ml-0 md:w-1/5 lg:px-4">
            <i class="fa-solid fa-chart-mixed" />
            <span class="select-none">&nbsp;&nbsp;View&nbsp;Analytics&nbsp;</span>
          </a>
          <a href="#/analytics/documents/send-email?id={query.documentId}" target="_blank" class="flex h-9 w-4/5 min-w-fit cursor-pointer items-center justify-center rounded-md border border-[#a0a1a580] bg-blue-500 px-2 text-sm font-medium text-white hover:bg-blue-600 hover:text-white focus:outline-none active:bg-blue-500 disabled:cursor-not-allowed disabled:bg-blue-200 sm:ml-0 md:w-1/5 lg:px-4">
            <i class="fa-solid fa-envelope" />
            <span class="select-none">&nbsp;&nbsp;Send&nbsp;Email&nbsp;</span>
          </a>
          <button type="button" on:click={startNew} class="flex h-9 w-4/5 min-w-fit cursor-pointer items-center justify-center rounded-md border border-[#a0a1a580] bg-yellow-500 px-2 text-sm font-medium text-white hover:bg-yellow-600 hover:text-white focus:outline-none active:bg-yellow-500 disabled:cursor-not-allowed disabled:bg-yellow-300 sm:ml-0 md:w-1/5 lg:px-4">
            <i class="fa-solid fa-upload" />
            <span class="select-none">&nbsp;&nbsp;Publish&nbsp;another&nbsp;file&nbsp;</span>
          </button>
        </div>
        <!-- loadingScreen -->
        {#if apiCallBtnDisable}
          <LoadingScreen msg={"Document ID revoking"} />
        {/if}
      </div>
    </div>
  </div>
  <Footer />
</div>
